import { useState, useMemo, useEffect, useCallback } from "react";
import * as XLSX from "xlsx";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Eye, Pencil, Phone, MessageCircle, FilePlus2, TrendingUp, Ban, RotateCcw, Trash2, Inbox, Plus,
  AlertTriangle, RefreshCw,
} from "lucide-react";
import DashboardLayout from "../components/DashboardLayout";
import Modal from "../components/Modal";
import type { ActionMenuItem } from "../components/ActionMenu";
import EnquiryForm from "./EnquiryForm";
import EnquiryDetailModal from "./EnquiryDetailModal";
import { enquiriesApi, clientsApi, apiErrorMessage } from "@/lib/api";
import { byNewest } from "@/lib/utils";
import { useToast } from "@/context/ToastContext";
import { useAuth } from "@/hooks/useAuth";
import { usePermission } from "@/context/PermissionContext";
import {
  EnquiryHeader,
  EnquiryFilters,
  EnquiryStats,
  EnquiryList,
  type EnqSortCol,
  type PrimaryAction,
} from "./components";
import {
  enquiryName, enquiryPhone, enquiryEmail, latestQuotation, matchesSearch, statusLabel,
  telHref, whatsappHref, ymd, type EnquiryData,
} from "./enquiry";

const statuses = ["all", "new", "qualified", "converted", "rejected"] as const;
type Status = typeof statuses[number];
type Enquiry = EnquiryData;

interface Client {
  id: number;
  name: string;
  phone?: string;
  email?: string;
}

interface EnquiryFormValues {
  client: string;
  contact: string;
  email: string;
  type: string;
  guests: string;
  date: string;
  leadSource: string;
  status: string;
  notes: string;
}

// Enquiry → edit form values
const mapToFormData = (e: Enquiry): EnquiryFormValues => ({
  client: enquiryName(e) === "Unknown" ? "" : enquiryName(e),
  contact: enquiryPhone(e),
  email: enquiryEmail(e),
  type: e.type || "",
  guests: e.guest_count ? String(e.guest_count) : "",
  date: ymd(e.event_date),
  leadSource: e.lead_source || "",
  status: e.status || "new",
  notes: e.notes || "",
});

// Form values → API fields (the enquiries table calls the event type `type`)
const mapToApiData = (f: EnquiryFormValues, clientId: number) => ({
  client_id: clientId,
  client_name: f.client.trim(),
  client_phone: f.contact.trim(),
  client_email: f.email.trim() || null,
  type: f.type,
  guest_count: parseInt(f.guests) || null,
  event_date: f.date || null,
  lead_source: f.leadSource,
  status: f.status,
  notes: f.notes,
});

export default function EnquiriesPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { showToast } = useToast();
  const { user } = useAuth();
  const { can } = usePermission();
  // Mirrors EnquiryPolicy::delete (staff and receptionists can't delete)
  const canDelete = ["super_admin", "admin", "master", "manager"].includes(user?.role || "");
  // "Create quotation" only for people who can open the Quotations page
  const canQuote = can("quotations");

  const [enquiries, setEnquiries] = useState<Enquiry[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [activeFilter, setActiveFilter] = useState<Status>("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [sortCol, setSortCol] = useState<EnqSortCol | null>(null);
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [editingEnquiry, setEditingEnquiry] = useState<Enquiry | null>(null);
  const [detailId, setDetailId] = useState<number | null>(null);
  const [detailRefresh, setDetailRefresh] = useState(0);
  const [deleting, setDeleting] = useState<Enquiry | null>(null);
  const [busy, setBusy] = useState(false);

  const loadEnquiries = useCallback(async (quiet = false) => {
    if (!quiet) setLoading(true);
    try {
      const response = await enquiriesApi.list();
      setEnquiries(response.data?.data || []);
      setLoadError(null);
    } catch (error) {
      if (quiet) showToast(apiErrorMessage(error, "Couldn't refresh the enquiries"), "error");
      else setLoadError(apiErrorMessage(error, "Couldn't load enquiries"));
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  const loadClients = async () => {
    try {
      const response = await clientsApi.list();
      setClients(response.data?.data || response.data || []);
    } catch {
      // Only used to avoid duplicate clients; ensureClient searches the API as a fallback
    }
  };

  useEffect(() => {
    loadEnquiries();
    loadClients();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // /enquiries?open=ID (old /enquiries/detail links, event page) opens that enquiry's popup
  useEffect(() => {
    const open = Number(searchParams.get("open"));
    if (!open) return;
    setDetailId(open);
    const next = new URLSearchParams(searchParams);
    next.delete("open");
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const statusCounts = useMemo(() => {
    const count = (s: string) => enquiries.filter(e => e.status === s).length;
    return {
      all: enquiries.length,
      new: count("new"),
      qualified: count("qualified"),
      converted: count("converted"),
      rejected: count("rejected"),
    } as Record<Status, number>;
  }, [enquiries]);

  const filtered = useMemo(() => {
    const list = enquiries.filter(e =>
      (activeFilter === "all" || e.status === activeFilter) && matchesSearch(e, searchQuery)
    );

    if (!sortCol) return [...list].sort(byNewest); // newest-received first

    const val = (e: Enquiry): string | number =>
      sortCol === "name" ? enquiryName(e).toLowerCase()
      : sortCol === "type" ? (e.type || "").toLowerCase()
      : sortCol === "date" ? ymd(e.event_date)
      : sortCol === "guests" ? Number(e.guest_count) || 0
      : sortCol === "status" ? e.status || ""
      : e.created_at || "";
    return [...list].sort((a, b) => {
      const x = val(a), y = val(b);
      const cmp = typeof x === "number" ? x - (y as number) : String(x).localeCompare(String(y));
      return sortDir === "asc" ? cmp : -cmp;
    });
  }, [activeFilter, searchQuery, enquiries, sortCol, sortDir]);

  const handleSort = (col: EnqSortCol) => {
    if (sortCol === col) setSortDir(d => (d === "asc" ? "desc" : "asc"));
    else { setSortCol(col); setSortDir("asc"); }
  };

  const clearFilters = () => { setActiveFilter("all"); setSearchQuery(""); setSortCol(null); };

  const exportToExcel = () => {
    const rows = [
      ["#", "Client Name", "Phone", "Email", "Event Type", "Event Date", "Guests", "Status", "Quotation", "Lead Source", "Received"],
      ...filtered.map((e, i) => [
        i + 1, enquiryName(e), enquiryPhone(e), enquiryEmail(e), e.type || "", ymd(e.event_date),
        Number(e.guest_count) || 0, statusLabel(e.status), latestQuotation(e)?.quotation_number || "",
        e.lead_source || "", ymd(e.created_at),
      ]),
    ];
    const ws = XLSX.utils.aoa_to_sheet(rows);
    ws["!cols"] = [{ wch: 5 }, { wch: 22 }, { wch: 16 }, { wch: 24 }, { wch: 18 }, { wch: 12 }, { wch: 8 }, { wch: 12 }, { wch: 14 }, { wch: 14 }, { wch: 12 }];
    ws["!views"] = [{ state: "frozen", xSplit: 0, ySplit: 1, topLeftCell: "A2", activeCell: "A2", sqref: "A2" }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Enquiries");
    XLSX.writeFile(wb, `enquiries_${new Date().toISOString().split("T")[0]}.xlsx`);
    showToast(`Exported ${filtered.length} enquiries to Excel`, "success");
  };

  // Finds an existing client by phone/email, creates one only if truly new
  const ensureClient = async (f: { client: string; contact: string; email: string }): Promise<number> => {
    const phone = f.contact?.trim() || "";
    const email = f.email?.trim() || "";

    const local = clients.find(c => (phone && c.phone === phone) || (email && c.email === email));
    if (local) return local.id;

    const searchTerm = phone || email || f.client;
    if (searchTerm) {
      try {
        const res = await clientsApi.list(`search=${encodeURIComponent(searchTerm)}&per_page=100`);
        const list: Client[] = Array.isArray(res.data?.data) ? res.data.data : Array.isArray(res.data) ? res.data : [];
        const exact = list.find(c => (phone && c.phone === phone) || (email && c.email === email));
        if (exact) return exact.id;
      } catch {
        // Search failed: fall through to create
      }
    }

    try {
      const res = await clientsApi.create({ name: f.client || "Unknown Client", phone: phone || null, email: email || null });
      const id = res.data?.id;
      if (!id) throw new Error("Could not read the new client's id");
      setClients(prev => [...prev, { id, name: f.client, phone, email }]);
      return id;
    } catch (err) {
      const errors = (err as { errors?: Record<string, string[]> })?.errors;
      if (errors?.phone) throw new Error("A client with this phone number already exists. Search for that client or use a different number.");
      if (errors?.email) throw new Error("A client with this email already exists. Search for that client or use a different email.");
      throw new Error(apiErrorMessage(err, "Couldn't create the client"));
    }
  };

  const handleAddEnquiry = async (formData: EnquiryFormValues) => {
    setBusy(true);
    try {
      const clientId = await ensureClient(formData);
      await enquiriesApi.create(mapToApiData(formData, clientId));
      showToast("Enquiry added", "success");
      setIsAddModalOpen(false);
      loadEnquiries(true);
    } catch (error) {
      showToast(apiErrorMessage(error, "Couldn't add the enquiry"), "error");
    } finally {
      setBusy(false);
    }
  };

  const handleEditEnquiry = async (formData: EnquiryFormValues) => {
    if (!editingEnquiry) return;
    setBusy(true);
    try {
      const clientId = editingEnquiry.client_id || await ensureClient(formData);
      await enquiriesApi.update(editingEnquiry.id, mapToApiData(formData, clientId));
      showToast("Enquiry updated", "success");
      setEditingEnquiry(null);
      setDetailRefresh(n => n + 1);
      loadEnquiries(true);
    } catch (error) {
      showToast(apiErrorMessage(error, "Couldn't update the enquiry"), "error");
    } finally {
      setBusy(false);
    }
  };

  const changeStatus = async (e: Enquiry, status: string) => {
    try {
      await enquiriesApi.update(e.id, { status });
      setEnquiries(prev => prev.map(x => (x.id === e.id ? { ...x, status } : x)));
      showToast(`Marked as ${statusLabel(status).toLowerCase()}`, "success");
    } catch (error) {
      showToast(apiErrorMessage(error, "Couldn't change the status"), "error");
    }
  };

  /**
   * Opens the quotation builder pre-filled from this enquiry. Nothing is created until the
   * quotation is saved; saving links it to the enquiry, which then becomes Converted.
   */
  const startQuotation = async (e: Enquiry) => {
    if (!e.client_id) {
      // The builder needs a client record; make (or find) one from the enquiry's contact details
      try {
        const clientId = await ensureClient({ client: enquiryName(e), contact: enquiryPhone(e), email: enquiryEmail(e) });
        await enquiriesApi.update(e.id, { client_id: clientId });
      } catch (error) {
        showToast(apiErrorMessage(error, "Couldn't create a client for this enquiry"), "error");
        return;
      }
    }
    navigate(`/quotations?from_enquiry=${e.id}`);
  };

  const confirmDelete = async () => {
    if (!deleting) return;
    setBusy(true);
    try {
      await enquiriesApi.delete(deleting.id);
      showToast(`Enquiry from ${enquiryName(deleting)} deleted`, "success");
      if (detailId === deleting.id) setDetailId(null);
      setDeleting(null);
      loadEnquiries(true);
    } catch (error) {
      showToast(apiErrorMessage(error, "Couldn't delete the enquiry"), "error");
    } finally {
      setBusy(false);
    }
  };

  const rejectInstead = async () => {
    if (!deleting) return;
    setBusy(true);
    await changeStatus(deleting, "rejected");
    setBusy(false);
    setDeleting(null);
    setDetailRefresh(n => n + 1);
  };

  const primaryAction = (e: Enquiry): PrimaryAction | null => {
    const q = latestQuotation(e);
    if (q && canQuote) return { label: "Open quotation", onClick: () => navigate(`/quotations/detail?id=${q.id}`) };
    if (e.status !== "rejected" && canQuote) return { label: "Create quotation", onClick: () => startQuotation(e) };
    return null;
  };

  const menuItems = (e: Enquiry): ActionMenuItem[] => {
    const phone = enquiryPhone(e);
    const tel = telHref(phone);
    const wa = whatsappHref(phone);
    const items: ActionMenuItem[] = [
      { label: "View details", icon: Eye, onClick: () => setDetailId(e.id) },
      { label: "Edit", icon: Pencil, onClick: () => setEditingEnquiry(e) },
    ];
    if (tel) items.push({ label: "Call", icon: Phone, hint: phone, onClick: () => { window.location.href = tel; } });
    if (wa) items.push({ label: "WhatsApp", icon: MessageCircle, onClick: () => { window.open(wa, "_blank", "noopener,noreferrer"); } });
    if (latestQuotation(e) && canQuote && e.status !== "rejected") {
      items.push({ label: "Create another quotation", icon: FilePlus2, onClick: () => startQuotation(e) });
    }
    if (e.status === "new") items.push({ label: "Mark qualified", icon: TrendingUp, separated: true, onClick: () => changeStatus(e, "qualified") });
    if (e.status === "new" || e.status === "qualified") {
      items.push({ label: "Mark rejected", icon: Ban, separated: e.status !== "new", onClick: () => changeStatus(e, "rejected") });
    }
    if (e.status === "rejected") items.push({ label: "Reopen", icon: RotateCcw, separated: true, onClick: () => changeStatus(e, "new") });
    if (canDelete) items.push({ label: "Delete…", icon: Trash2, danger: true, separated: true, onClick: () => setDeleting(e) });
    return items;
  };

  // ─── Loading / error ─────────────────────────────────────────────────────

  if (loading) {
    return (
      <DashboardLayout>
        <div className="h-12 w-64 max-w-full rounded-xl bg-stone-100 animate-pulse mb-4" />
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4" aria-hidden="true">
          {[0, 1, 2, 3].map(i => <div key={i} className="h-[76px] rounded-xl bg-stone-100 animate-pulse" />)}
        </div>
        <div className="h-14 rounded-xl bg-stone-100 animate-pulse mb-4" />
        <div className="bg-white border border-stone-200 rounded-xl divide-y divide-stone-100" aria-label="Loading enquiries">
          {[0, 1, 2, 3, 4].map(i => (
            <div key={i} className="p-4 flex gap-4 items-center">
              <div className="flex-1 space-y-2"><div className="h-4 w-1/3 rounded bg-stone-100 animate-pulse" /><div className="h-3 w-1/4 rounded bg-stone-100 animate-pulse" /></div>
              <div className="h-4 w-20 rounded bg-stone-100 animate-pulse" />
            </div>
          ))}
        </div>
      </DashboardLayout>
    );
  }

  if (loadError) {
    return (
      <DashboardLayout>
        <div className="bg-white border border-stone-200 rounded-xl p-10 text-center">
          <AlertTriangle className="w-10 h-10 text-amber-500 mx-auto mb-3" />
          <p className="font-semibold text-stone-800">Couldn't load enquiries</p>
          <p className="text-sm text-stone-500 mt-1">{loadError}</p>
          <button type="button" onClick={() => loadEnquiries()} className="mt-4 h-10 inline-flex items-center gap-2 px-4 rounded-xl border border-stone-200 text-sm font-semibold text-stone-700 hover:bg-stone-50">
            <RefreshCw className="w-4 h-4" /> Try again
          </button>
        </div>
      </DashboardLayout>
    );
  }

  const emptyState = (
    <div className="text-center py-12 px-4">
      <Inbox className="w-12 h-12 mx-auto mb-3 text-stone-300" />
      {enquiries.length === 0 ? (
        <>
          <p className="text-stone-600 font-medium">No enquiries yet</p>
          <p className="text-sm text-stone-400 mt-1">Enquiries from the website form appear here too.</p>
          <button type="button" onClick={() => setIsAddModalOpen(true)} className="mt-4 h-10 inline-flex items-center gap-2 px-4 rounded-xl text-white font-semibold text-sm bg-amber-500 hover:bg-amber-600">
            <Plus className="w-4 h-4" /> Add enquiry
          </button>
        </>
      ) : (
        <>
          <p className="text-stone-600 font-medium">No enquiries match</p>
          <button type="button" onClick={clearFilters} className="mt-3 min-h-10 px-3 text-sm font-semibold text-amber-700 hover:underline">Clear filters</button>
        </>
      )}
    </div>
  );

  const deletingBlocked = !!deleting?.event;
  const deletingQuotes = deleting?.quotations || [];

  return (
    <DashboardLayout>
      <EnquiryHeader
        onAddClick={() => setIsAddModalOpen(true)}
        onExport={exportToExcel}
        exportDisabled={filtered.length === 0}
      />

      {/* Tiles filter the list */}
      <EnquiryStats
        totalCount={enquiries.length}
        newCount={statusCounts.new}
        qualifiedCount={statusCounts.qualified}
        convertedCount={statusCounts.converted}
        active={activeFilter}
        onSelect={(s) => { setActiveFilter(s); setSortCol(null); }}
      />

      <EnquiryFilters
        statuses={statuses}
        activeFilter={activeFilter}
        onFilterChange={setActiveFilter}
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        statusCounts={statusCounts}
      />

      <EnquiryList
        enquiries={filtered}
        onOpen={setDetailId}
        primaryAction={primaryAction}
        menuItems={menuItems}
        emptyState={emptyState}
        sortCol={sortCol}
        sortDir={sortDir}
        onSort={handleSort}
      />
      {filtered.length > 0 && (
        <p className="-mt-2 text-xs text-stone-400">
          {filtered.length} of {enquiries.length} enquiries · An enquiry becomes Converted when a quotation is saved for it
        </p>
      )}

      {/* Details popup (row / card click, ?open=ID) */}
      <EnquiryDetailModal
        isOpen={detailId !== null}
        enquiryId={detailId}
        refreshKey={detailRefresh}
        onClose={() => setDetailId(null)}
        onEdit={setEditingEnquiry}
        onCreateQuotation={startQuotation}
        onDelete={setDeleting}
        onChanged={() => loadEnquiries(true)}
        canQuote={canQuote}
        canDelete={canDelete}
      />

      <Modal isOpen={isAddModalOpen} onClose={() => !busy && setIsAddModalOpen(false)} title="Add enquiry" size="md">
        <EnquiryForm onSubmit={handleAddEnquiry} onCancel={() => setIsAddModalOpen(false)} />
      </Modal>

      <Modal isOpen={!!editingEnquiry} onClose={() => !busy && setEditingEnquiry(null)} title="Edit enquiry" size="md">
        {editingEnquiry && (
          <EnquiryForm
            initialData={mapToFormData(editingEnquiry)}
            isEdit={true}
            onSubmit={handleEditEnquiry}
            onCancel={() => setEditingEnquiry(null)}
          />
        )}
      </Modal>

      {deleting && (
        <Modal
          isOpen={!!deleting}
          onClose={() => !busy && setDeleting(null)}
          title={deletingBlocked ? "Can't delete this enquiry" : `Delete enquiry from ${enquiryName(deleting)}?`}
          size="sm"
          footer={
            <div className="flex flex-col-reverse sm:flex-row gap-2 sm:justify-end">
              <button type="button" onClick={() => setDeleting(null)} disabled={busy} className="h-11 px-4 rounded-xl border border-stone-300 text-sm font-semibold text-stone-600 hover:bg-stone-50">
                {deletingBlocked ? "OK" : "Keep enquiry"}
              </button>
              {!deletingBlocked && (deleting.status === "new" || deleting.status === "qualified") && (
                <button type="button" onClick={rejectInstead} disabled={busy} className="h-11 px-4 rounded-xl border border-stone-300 text-sm font-semibold text-stone-700 hover:bg-stone-50">
                  Mark rejected instead
                </button>
              )}
              {!deletingBlocked && (
                <button type="button" onClick={confirmDelete} disabled={busy} className="h-11 px-4 rounded-xl bg-red-600 hover:bg-red-700 text-white text-sm font-semibold disabled:opacity-50">
                  Delete permanently
                </button>
              )}
            </div>
          }
        >
          <div className="space-y-3 text-sm text-stone-600">
            {deletingBlocked ? (
              <p>This enquiry already became the event “{deleting.event?.name}”, so it stays on record.</p>
            ) : (
              <>
                <p>This permanently removes the enquiry{deleting.type ? ` (${deleting.type}${deleting.event_date ? `, ${ymd(deleting.event_date)}` : ""})` : ""}.</p>
                {deletingQuotes.length > 0 && (
                  <p>
                    Its quotation{deletingQuotes.length === 1 ? "" : "s"} {deletingQuotes.map(q => q.quotation_number || `#${q.id}`).join(", ")} will be kept, just no longer linked to an enquiry.
                  </p>
                )}
                {(deleting.status === "new" || deleting.status === "qualified") && (
                  <p className="rounded-lg bg-amber-50 text-amber-800 px-3 py-2">If the client just isn't going ahead, mark it rejected instead. It then still counts in the enquiry report.</p>
                )}
              </>
            )}
          </div>
        </Modal>
      )}
    </DashboardLayout>
  );
}
